import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from './SectionHeader';
import {
  type CurrentPolicyContext,
  RiskClass,
  CalculationMethod,
  BenefitType,
  Currency,
  type BenefitConfig,
  BasisOfCover,
  type Category,
  type ClientProfile,
  type PolicyParameters,
} from '../../types';
import { CURRENCY_SYMBOLS } from '../../constants';
import { useToast } from '../ui/Toast';
import { lhrApiService } from '../../services/lhrApiService';
import { cortexApi, isApiConfigured } from '../../services/cortexApi';
import { MoneyInput } from '../ui/MoneyInput';

// AI-driven features (auto-enrich, identity verification modal, claims AI
// analysis, smart import) were archived in v0.x-prerefactor-snapshot.
// See docs/v2/risk-intelligence.md and docs/v2/proposal-wizard.md.

interface QuoteEngineProps {
  context: CurrentPolicyContext;
  onUpdate: (ctx: CurrentPolicyContext) => void;
  isCalculating: boolean;
}

type VatMode = 'Registered' | 'NonVAT' | 'TBA';

const newId = (): string => crypto.randomUUID();

const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
  e.target.select();
};

export const CommercialPAWorkbench: React.FC<QuoteEngineProps> = ({
  context,
  onUpdate,
  isCalculating,
}) => {
  const { showToast: _showToast } = useToast();
  const [showClaimsHistory, setShowClaimsHistory] = useState(false);

  const config = lhrApiService.getConfig();

  const brokerQuery = useQuery({
    queryKey: ['entities', 'brokerage', 'Active'],
    queryFn: () => cortexApi.entities.list({ entityTypeSlug: 'brokerage', status: 'Active' }),
    enabled: isApiConfigured(),
    staleTime: 5 * 60 * 1000,
  });
  const brokers = brokerQuery.data?.entities ?? [];

  const updateClient = <K extends keyof ClientProfile>(field: K, value: ClientProfile[K]): void => {
    onUpdate({
      ...context,
      client_profile: { ...context.client_profile, [field]: value },
    });
  };

  const updateParams = <K extends keyof PolicyParameters>(
    field: K,
    value: PolicyParameters[K],
  ): void => {
    onUpdate({
      ...context,
      policy_parameters: { ...context.policy_parameters, [field]: value },
    });
  };

  // Auto-populate internal notes with the system-derived pricing rationale.
  // Only writes when notes are empty so user edits are never overwritten.
  useEffect(() => {
    const summary = context.pricing_summary;
    const volDisc = summary.volumeDiscountUsed;
    const warnings = summary.warnings;

    let note = 'Pricing Rationale:\n';
    if (volDisc && volDisc.discount > 0) {
      note += `• Volume Discount: ${volDisc.discount}% applied based on ${volDisc.source} scale.\n`;
    } else {
      note += `• Volume Discount: None applicable (Standard Rates).\n`;
    }
    if (warnings.length > 0) {
      note += `• Warnings: ${warnings.join(', ')}\n`;
    }
    context.categories.forEach((cat) => {
      if (cat.discretionaryAdjustment) {
        note += `• ${cat.name}: Manual adjustment of ${cat.discretionaryAdjustment}% applied.\n`;
      }
    });

    if (!context.client_profile.underwritingNotes) {
      updateClient('underwritingNotes', note);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.pricing_summary.grossPremium]);

  const addClaimsRow = (): void => {
    const currentData = context.client_profile.claimsData ?? [];
    const last = currentData[currentData.length - 1];
    const lastYear = last ? last.year : new Date().getFullYear();
    const newData = [...currentData, { year: lastYear - 1, paid: 0, reserved: 0 }];
    onUpdate({
      ...context,
      client_profile: { ...context.client_profile, claimsData: newData },
    });
  };

  const updateClaimsRow = (idx: number, field: 'year' | 'paid' | 'reserved', val: number): void => {
    if (!context.client_profile.claimsData) return;
    const newData = [...context.client_profile.claimsData];
    const existing = newData[idx];
    if (!existing) return;
    newData[idx] = { ...existing, [field]: val };
    onUpdate({
      ...context,
      client_profile: { ...context.client_profile, claimsData: newData },
    });
  };

  const removeClaimsRow = (idx: number): void => {
    if (!context.client_profile.claimsData) return;
    const newData = context.client_profile.claimsData.filter((_, i) => i !== idx);
    onUpdate({
      ...context,
      client_profile: { ...context.client_profile, claimsData: newData },
    });
  };

  const addCategory = (): void => {
    const sectorDef = config.sectors.find((s) => s.name === context.client_profile.sector);
    const defaultRisk = sectorDef ? sectorDef.defaultRisk : RiskClass.Risk2;

    const defaultBenefits: BenefitConfig[] = [
      BenefitType.Death,
      BenefitType.PTD,
      BenefitType.TTD,
      BenefitType.Medical,
    ].map((type) => ({
      id: newId(),
      type,
      isEnabled: true,
      netPremium: 0,
      multiple: 1,
      salaryPct: 100,
      sumInsured: 100000,
      weeklyBenefit: 1000,
      benefitPeriod: 104,
      excessDays: 7,
      medicalExcess: 500,
      medicalLimit: 50000,
      discretionaryAdjustment: 0,
    }));

    const newCat: Category = {
      id: newId(),
      name: `Category ${context.categories.length + 1}`,
      method: CalculationMethod.Stated,
      riskClass: defaultRisk,
      basis: BasisOfCover.TwentyFourHour,
      headcount: 0,
      wageroll: 0,
      discretionaryAdjustment: 0,
      customBasisAdjustment: 0,
      benefits: defaultBenefits,
    };
    onUpdate({ ...context, categories: [...context.categories, newCat] });
  };

  const removeCategory = (index: number): void => {
    onUpdate({ ...context, categories: context.categories.filter((_, i) => i !== index) });
  };

  const updateCategory = <K extends keyof Category>(
    index: number,
    field: K,
    value: Category[K],
  ): void => {
    const newCats = [...context.categories];
    const existing = newCats[index];
    if (!existing) return;
    newCats[index] = { ...existing, [field]: value };
    onUpdate({ ...context, categories: newCats });
  };

  const updateBenefit = <K extends keyof BenefitConfig>(
    catIndex: number,
    benIndex: number,
    field: K,
    value: BenefitConfig[K],
  ): void => {
    const newCats = [...context.categories];
    const cat = newCats[catIndex];
    if (!cat) return;
    const newBenefits = [...cat.benefits];
    const existing = newBenefits[benIndex];
    if (!existing) return;
    newBenefits[benIndex] = { ...existing, [field]: value };
    newCats[catIndex] = { ...cat, benefits: newBenefits };
    onUpdate({ ...context, categories: newCats });
  };

  const inputBaseClass =
    'w-full h-8 py-1 px-3 bg-slate-50 border border-slate-300 rounded text-xs font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-hr-pink focus:border-hr-pink outline-none transition-colors shadow-sm';
  const selectBaseClass =
    'h-8 py-1 px-2 border border-slate-300 rounded text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-hr-pink outline-none shadow-sm bg-slate-50 focus:bg-white';
  const gridLayoutClass = 'grid grid-cols-[100px_1fr_100px_140px_100px_20px] gap-2 items-center';

  const getVatMode = (): VatMode => {
    const { isVatRegistered, vatNumber } = context.client_profile;
    if (!isVatRegistered) return 'NonVAT';
    if (vatNumber === 'TBA') return 'TBA';
    return 'Registered';
  };

  const handleVatModeChange = (mode: VatMode): void => {
    const updates: Partial<ClientProfile> = {};
    let vatEnabled = true;
    if (mode === 'Registered') {
      updates.isVatRegistered = true;
      updates.vatNumber =
        context.client_profile.vatNumber === 'TBA' ||
        context.client_profile.vatNumber === 'Non-VAT Vendor'
          ? ''
          : context.client_profile.vatNumber;
    } else if (mode === 'NonVAT') {
      updates.isVatRegistered = false;
      updates.vatNumber = 'Non-VAT Vendor';
      vatEnabled = false;
    } else {
      updates.isVatRegistered = true;
      updates.vatNumber = 'TBA';
    }
    onUpdate({
      ...context,
      client_profile: { ...context.client_profile, ...updates },
      policy_parameters: { ...context.policy_parameters, vatEnabled },
    });
  };

  const currentVatMode = getVatMode();

  return (
    <div className="space-y-10 pb-24 font-sans relative">
      {/* SECTION 1: CLIENT CONTEXT */}
      <section id="client-context" className="scroll-mt-36">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-soft space-y-6">
          <SectionHeader number="1" title="Client Context" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div className="md:col-span-2">
              <label
                htmlFor="company-name"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
              >
                Company Name
              </label>
              <input
                id="company-name"
                type="text"
                className="w-full h-10 px-3 bg-white border border-slate-300 rounded text-xs font-bold text-slate-900 focus:ring-2 focus:ring-hr-pink outline-none shadow-sm"
                placeholder="Enter Company Name"
                value={context.client_profile.companyName}
                onChange={(e) => updateClient('companyName', e.target.value)}
                onFocus={handleFocus}
              />
            </div>
            <div>
              <label
                htmlFor="company-website"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
              >
                Website
              </label>
              <input
                id="company-website"
                type="text"
                className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded text-xs text-slate-700 focus:bg-white focus:ring-2 focus:ring-hr-pink outline-none shadow-sm"
                placeholder="www.example.com"
                value={context.client_profile.website ?? ''}
                onChange={(e) => updateClient('website', e.target.value)}
              />
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded border border-slate-200">
            <div className="w-full max-w-md">
              <label
                htmlFor="broker-select"
                className="block text-micro font-bold text-hr-indigo uppercase tracking-wide mb-1"
              >
                Broker / Intermediary
              </label>
              <select
                id="broker-select"
                className={`${selectBaseClass} w-full border-indigo-200 focus:border-hr-indigo`}
                value={context.client_profile.brokerName}
                onChange={(e) => updateClient('brokerName', e.target.value)}
              >
                <option value="">Select Broker...</option>
                {brokers.map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="sector-select"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
              >
                Industry Sector
              </label>
              <select
                id="sector-select"
                className={`${selectBaseClass} w-full`}
                value={context.client_profile.sector}
                onChange={(e) => updateClient('sector', e.target.value)}
              >
                <option value="">Select Sector...</option>
                {config.sectors.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
              {(() => {
                const selected = config.sectors.find(
                  (s) => s.name === context.client_profile.sector,
                );
                if (!selected) return null;
                return (
                  <div className="mt-1">
                    <p className="text-xs text-slate-500 leading-snug">{selected.description}</p>
                    {selected.longDescription ? (
                      <details className="mt-0.5">
                        <summary className="text-xs font-semibold text-hr-indigo cursor-pointer select-none">
                          More info
                        </summary>
                        <p className="mt-1 text-xs text-slate-500 leading-snug">
                          {selected.longDescription}
                        </p>
                      </details>
                    ) : null}
                  </div>
                );
              })()}
            </div>

            <div>
              <span className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1">
                VAT Registration
              </span>
              <div
                role="radiogroup"
                aria-label="VAT registration status"
                className="bg-slate-100 p-1 rounded-t flex justify-between border-b border-slate-200"
              >
                {(['Registered', 'NonVAT', 'TBA'] as const).map((mode) => (
                  <label
                    key={mode}
                    className="flex items-center gap-1 cursor-pointer px-2 py-1 rounded hover:bg-white transition-colors"
                  >
                    <input
                      type="radio"
                      name="vatStatus"
                      checked={currentVatMode === mode}
                      onChange={() => handleVatModeChange(mode)}
                      className="text-hr-pink focus:ring-hr-pink"
                    />
                    <span
                      className={`text-micro font-bold ${
                        currentVatMode === mode ? 'text-hr-indigo' : 'text-slate-500'
                      }`}
                    >
                      {mode === 'NonVAT' ? 'Non-Vendor' : mode}
                    </span>
                  </label>
                ))}
              </div>
              <div
                className={`h-10 border border-t-0 border-slate-300 rounded-b flex items-center px-3 ${
                  currentVatMode === 'Registered' ? 'bg-white' : 'bg-slate-50'
                }`}
              >
                {currentVatMode === 'Registered' ? (
                  <input
                    type="text"
                    aria-label="VAT number"
                    className="w-full h-full outline-none text-xs font-bold text-slate-800 bg-transparent placeholder-slate-300"
                    placeholder="Enter 10-digit VAT Number..."
                    value={context.client_profile.vatNumber}
                    onChange={(e) => updateClient('vatNumber', e.target.value)}
                  />
                ) : (
                  <span className="text-micro font-bold text-slate-400 italic w-full text-center">
                    {currentVatMode === 'NonVAT' ? 'Exempt / Not Registered' : 'To Be Advised'}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="company-address"
                className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
              >
                Physical Address
              </label>
              <input
                id="company-address"
                type="text"
                className={inputBaseClass}
                placeholder="Head Office Address..."
                value={context.client_profile.address}
                onChange={(e) => updateClient('address', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="business-description"
              className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
            >
              Business Description
            </label>
            <textarea
              id="business-description"
              className="w-full h-24 py-2 px-3 bg-slate-50 border border-slate-300 rounded text-slate-800 text-xs font-medium focus:bg-white focus:ring-2 focus:ring-hr-pink outline-none resize-none shadow-sm transition-colors"
              placeholder="Describe business operations..."
              value={context.client_profile.businessDescription}
              onChange={(e) => updateClient('businessDescription', e.target.value)}
              onFocus={handleFocus}
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: CLAIMS EXPERIENCE */}
      <section id="claims-experience" className="scroll-mt-36">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-soft">
          <div className="flex justify-between items-center mb-6">
            <SectionHeader number="2" title="Claims Experience" />
            <div className="flex bg-slate-100 p-1 rounded border border-slate-200" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={!showClaimsHistory}
                onClick={() => {
                  setShowClaimsHistory(false);
                  updateClient(
                    'claimsHistory',
                    'Subject to no known or reported losses of last 3 years.',
                  );
                }}
                className={`px-3 py-1 text-micro font-bold rounded transition-colors ${
                  !showClaimsHistory
                    ? 'bg-white shadow text-slate-800'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Clean
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={showClaimsHistory}
                onClick={() => setShowClaimsHistory(true)}
                className={`px-3 py-1 text-micro font-bold rounded transition-colors ${
                  showClaimsHistory
                    ? 'bg-white shadow text-red-600'
                    : 'text-slate-500 hover:text-red-600'
                }`}
              >
                Adverse
              </button>
            </div>
          </div>

          {!showClaimsHistory ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-6 flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 text-emerald-600 font-bold">
                ✓
              </div>
              <div>
                <h4 className="text-xs font-bold text-emerald-900">Clean Loss Ratio Confirmed</h4>
                <p className="text-micro text-emerald-700 mt-1">
                  Standard confirmation: No known or reported losses in last 3 years.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-micro font-bold text-slate-500 uppercase tracking-wide">
                  3-Year Loss History
                </h4>
                <button
                  type="button"
                  onClick={addClaimsRow}
                  className="text-micro font-bold text-hr-indigo hover:underline"
                >
                  + Add Year
                </button>
              </div>
              <div className="bg-slate-50 rounded border border-slate-200 overflow-hidden shadow-sm">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 text-slate-500 font-bold border-b border-slate-200">
                    <tr>
                      <th className="p-3 text-micro uppercase">Year</th>
                      <th className="p-3 text-right text-micro uppercase">Paid</th>
                      <th className="p-3 text-right text-micro uppercase">Reserved</th>
                      <th className="p-3 w-10" aria-label="Row controls" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {context.client_profile.claimsData?.map((row, i) => (
                      <tr key={`${row.year}-${i}`} className="bg-white">
                        <td className="p-2">
                          <input
                            type="number"
                            aria-label="Year"
                            className="w-16 font-bold text-slate-700 border-none focus:ring-0 bg-transparent outline-none"
                            value={row.year}
                            onChange={(e) => updateClaimsRow(i, 'year', Number(e.target.value))}
                            onFocus={handleFocus}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <MoneyInput
                            value={row.paid}
                            onChange={(v) => updateClaimsRow(i, 'paid', v)}
                            className="text-right border-none bg-transparent"
                          />
                        </td>
                        <td className="p-2 text-right">
                          <MoneyInput
                            value={row.reserved}
                            onChange={(v) => updateClaimsRow(i, 'reserved', v)}
                            className="text-right border-none bg-transparent"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            aria-label={`Remove year ${row.year}`}
                            onClick={() => removeClaimsRow(i)}
                            className="text-slate-300 hover:text-red-500"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!context.client_profile.claimsData ||
                      context.client_profile.claimsData.length === 0) && (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-4 text-center text-slate-400 italic text-micro"
                        >
                          No Data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div>
                <label
                  htmlFor="claims-narrative"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Quote Narrative
                </label>
                <input
                  id="claims-narrative"
                  type="text"
                  className={inputBaseClass}
                  value={context.client_profile.claimsHistory}
                  onChange={(e) => updateClient('claimsHistory', e.target.value)}
                  onFocus={handleFocus}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 3: POLICY PARAMETERS */}
      <section id="policy-params" className="scroll-mt-36">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-soft">
          <SectionHeader number="3" title="Policy Configuration" />
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label
                  htmlFor="start-date"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Start Date
                </label>
                <input
                  id="start-date"
                  type="date"
                  className={`${selectBaseClass} w-full`}
                  value={context.policy_parameters.startDate}
                  onChange={(e) => updateParams('startDate', e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="period-months"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Period (Months)
                </label>
                <input
                  id="period-months"
                  type="number"
                  className={inputBaseClass}
                  value={context.policy_parameters.periodMonths}
                  onChange={(e) => updateParams('periodMonths', Number(e.target.value))}
                  onFocus={handleFocus}
                />
              </div>
              <div>
                <label
                  htmlFor="territorial-limits"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Territorial Limits
                </label>
                <input
                  id="territorial-limits"
                  type="text"
                  className={inputBaseClass}
                  value={context.policy_parameters.territorialLimits}
                  onChange={(e) => updateParams('territorialLimits', e.target.value)}
                  onFocus={handleFocus}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <span className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1">
                  Any One Life Limit
                </span>
                <MoneyInput
                  value={context.policy_parameters.aolLimit}
                  onChange={(v) => updateParams('aolLimit', v)}
                  className="h-8 text-xs bg-slate-50"
                />
              </div>
              <div>
                <span className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1">
                  Accumulation Limit
                </span>
                <MoneyInput
                  value={context.policy_parameters.aoaLimit}
                  onChange={(v) => updateParams('aoaLimit', v)}
                  className="h-8 text-xs bg-slate-50"
                />
              </div>
              <div>
                <label
                  htmlFor="currency-select"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Currency
                </label>
                <select
                  id="currency-select"
                  className={`${selectBaseClass} w-full`}
                  value={context.policy_parameters.currency}
                  onChange={(e) => updateParams('currency', e.target.value as Currency)}
                >
                  {Object.values(Currency).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
              <div>
                <label
                  htmlFor="policy-adjustment"
                  className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                >
                  Policy Adjustment (Load/Discount)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="policy-adjustment"
                    type="number"
                    className="w-24 h-8 px-3 bg-slate-50 border border-slate-300 rounded text-slate-800 text-right shadow-sm focus:bg-white focus:ring-2 focus:ring-hr-pink outline-none font-bold text-xs"
                    value={context.policy_parameters.discretionaryAdjustment ?? 0}
                    onChange={(e) =>
                      updateParams('discretionaryAdjustment', parseFloat(e.target.value))
                    }
                    onFocus={handleFocus}
                  />
                  <span className="text-xs font-bold text-slate-400">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: RATING ENGINE */}
      <section id="rating-engine" className="scroll-mt-36">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-soft">
          <SectionHeader number="4" title="Rating Engine" />

          <div className="space-y-6">
            {context.categories.map((cat, catIdx) => {
              const categoryTotal = cat.benefits.reduce((sum, b) => sum + (b.netPremium || 0), 0);
              const volDiscount = context.pricing_summary.volumeDiscountUsed;
              const isVolDiscountApplied = volDiscount && volDiscount.discount > 0;

              let footerRateDisplay = '';
              let footerExposureDisplay = '';

              if (cat.method === CalculationMethod.Stated) {
                const rate = cat.wageroll > 0 ? (categoryTotal / cat.wageroll) * 100 : 0;
                footerRateDisplay = `${rate.toFixed(4)}%`;
                footerExposureDisplay = `${CURRENCY_SYMBOLS[context.policy_parameters.currency]} ${cat.wageroll.toLocaleString()}`;
              } else {
                const rate = cat.headcount > 0 ? categoryTotal / cat.headcount : 0;
                footerRateDisplay = `${CURRENCY_SYMBOLS[context.policy_parameters.currency]} ${rate.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                footerExposureDisplay = `${cat.headcount} Lives`;
              }

              return (
                <div
                  key={cat.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden transition-shadow hover:shadow-lg"
                >
                  <div className="bg-slate-50/80 p-4 border-b border-slate-200 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between border-l-4 border-l-hr-indigo">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <span className="text-slate-300 font-bold text-lg flex-shrink-0">
                        #{catIdx + 1}
                      </span>
                      <input
                        type="text"
                        aria-label="Category name"
                        className="font-bold text-lg text-hr-indigo bg-transparent border-none focus:ring-0 p-0 w-full min-w-[150px]"
                        value={cat.name}
                        onChange={(e) => updateCategory(catIdx, 'name', e.target.value)}
                        onFocus={handleFocus}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        aria-label="Calculation method"
                        className={`${selectBaseClass} w-32`}
                        value={cat.method}
                        onChange={(e) =>
                          updateCategory(catIdx, 'method', e.target.value as CalculationMethod)
                        }
                      >
                        {Object.values(CalculationMethod).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <div className="flex gap-2 items-center bg-slate-50 rounded border border-slate-300 p-0.5 shadow-sm">
                        <select
                          aria-label="Basis of cover"
                          className="h-6 py-0 px-1 border-none text-xs font-semibold text-slate-800 focus:ring-0 bg-transparent w-auto outline-none"
                          value={cat.basis}
                          onChange={(e) =>
                            updateCategory(catIdx, 'basis', e.target.value as BasisOfCover)
                          }
                        >
                          {Object.values(BasisOfCover).map((b) => (
                            <option key={b} value={b}>
                              {b}
                            </option>
                          ))}
                        </select>
                        {cat.basis === BasisOfCover.Other && (
                          <div className="flex items-center border-l border-slate-200 pl-2 pr-1">
                            <span className="text-micro font-bold text-slate-400 mr-1">Disc %</span>
                            <input
                              type="number"
                              aria-label="Custom basis discount percent"
                              className="w-10 h-6 p-0 text-right text-xs font-bold border-none focus:ring-0 bg-white rounded"
                              value={cat.customBasisAdjustment ?? 0}
                              onChange={(e) =>
                                updateCategory(
                                  catIdx,
                                  'customBasisAdjustment',
                                  parseFloat(e.target.value),
                                )
                              }
                              placeholder="0"
                            />
                          </div>
                        )}
                      </div>
                      <select
                        aria-label="Risk class"
                        className={`${selectBaseClass} w-32`}
                        value={cat.riskClass}
                        onChange={(e) =>
                          updateCategory(catIdx, 'riskClass', e.target.value as RiskClass)
                        }
                      >
                        {Object.values(RiskClass).map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        aria-label={`Remove ${cat.name}`}
                        onClick={() => removeCategory(catIdx)}
                        className="text-slate-400 hover:text-red-500 p-1"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                      <div>
                        <label
                          htmlFor={`headcount-${cat.id}`}
                          className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1"
                        >
                          Headcount
                        </label>
                        <input
                          id={`headcount-${cat.id}`}
                          type="number"
                          className="w-full h-8 py-1 px-3 bg-slate-50 border border-slate-300 rounded text-xs font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-hr-pink focus:border-hr-pink outline-none transition-colors shadow-sm"
                          value={cat.headcount}
                          onChange={(e) =>
                            updateCategory(catIdx, 'headcount', parseInt(e.target.value, 10) || 0)
                          }
                          onFocus={handleFocus}
                        />
                      </div>
                      {cat.method === CalculationMethod.Stated && (
                        <div>
                          <span className="block text-micro font-bold text-slate-500 uppercase tracking-wide mb-1">
                            Annual Wageroll
                          </span>
                          <MoneyInput
                            value={cat.wageroll}
                            onChange={(v) => updateCategory(catIdx, 'wageroll', v)}
                            className="h-8 text-xs bg-slate-50"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-6 text-xs text-slate-600 bg-slate-50 p-3 rounded border border-slate-200 mb-6 justify-between">
                      <div className="flex items-center gap-4">
                        <div className="font-bold text-slate-400 uppercase tracking-wide text-micro">
                          Applied Discounts
                        </div>
                        {isVolDiscountApplied && (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded border border-emerald-200 text-xs font-bold">
                            Volume: -{volDiscount?.discount}% ({volDiscount?.source})
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase text-slate-500">Basis:</span>
                          <strong className="text-slate-800">
                            {cat.systemBasisFactor
                              ? `${(cat.systemBasisFactor * 100).toFixed(0)}%`
                              : '100%'}
                          </strong>
                          {cat.basis === BasisOfCover.Other && cat.customBasisAdjustment ? (
                            <span className="text-xs text-hr-pink font-bold">
                              (-{cat.customBasisAdjustment}%)
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 border-l border-slate-200 pl-4">
                        <span className="text-xs font-bold uppercase text-slate-500">
                          Category User Adj:
                        </span>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            aria-label="Category discretionary adjustment"
                            className="w-12 text-right border-b border-slate-300 bg-transparent focus:outline-none font-bold text-slate-800 text-xs"
                            value={cat.discretionaryAdjustment ?? 0}
                            onChange={(e) =>
                              updateCategory(
                                catIdx,
                                'discretionaryAdjustment',
                                parseFloat(e.target.value),
                              )
                            }
                            onFocus={handleFocus}
                          />
                          <span className="font-bold">%</span>
                        </div>
                      </div>
                    </div>

                    <div className={`${gridLayoutClass} mb-2 px-4`}>
                      <div className="text-micro font-bold text-slate-400 uppercase">Benefit</div>
                      <div className="text-micro font-bold text-slate-400 uppercase">Inputs</div>
                      <div className="text-micro font-bold text-slate-400 uppercase text-center">
                        Adjustments
                      </div>
                      <div className="text-micro font-bold text-slate-400 uppercase text-center">
                        Final Calculation
                      </div>
                      <div className="text-micro font-bold text-slate-400 uppercase text-right">
                        Premium
                      </div>
                    </div>

                    <div className="space-y-3">
                      {cat.benefits.map((ben, benIdx) => {
                        const isEnabled = ben.isEnabled !== false;

                        let exposure = 0;
                        let effectiveRateDisplay = '';
                        if (cat.method === CalculationMethod.Stated) {
                          exposure = cat.wageroll;
                          const rate = exposure > 0 ? (ben.netPremium / exposure) * 100 : 0;
                          effectiveRateDisplay = `${rate.toFixed(4)}%`;
                        } else {
                          exposure = cat.headcount;
                          const rate = exposure > 0 ? ben.netPremium / exposure : 0;
                          effectiveRateDisplay = `${CURRENCY_SYMBOLS[context.policy_parameters.currency]} ${rate.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                        }

                        let baseGross = 0;
                        if (cat.method === CalculationMethod.Stated) {
                          if (ben.type === BenefitType.Medical) {
                            const isFixed = ben.medicalBasis === 'Fixed';
                            baseGross = isFixed
                              ? cat.headcount * (ben.grossRate ?? 0)
                              : cat.wageroll * (ben.grossRate ?? 0);
                          } else {
                            baseGross = cat.wageroll * ((ben.grossRate ?? 0) / 100);
                          }
                        } else {
                          if (ben.type === BenefitType.Medical) {
                            baseGross = cat.headcount * (ben.grossRate ?? 0);
                          } else if (ben.type === BenefitType.TTD) {
                            const ttdExposure = cat.headcount * (ben.weeklyBenefit ?? 0);
                            baseGross = ttdExposure * ((ben.grossRate ?? 0) / 100);
                          } else {
                            const capExp = cat.headcount * (ben.sumInsured ?? 0);
                            baseGross = capExp * ((ben.grossRate ?? 0) / 100);
                          }
                        }
                        const netMultiplier = baseGross > 0 ? ben.netPremium / baseGross : 1;
                        const discountPct = (1 - netMultiplier) * 100;

                        return (
                          <div
                            key={ben.id}
                            className={`${gridLayoutClass} p-3 border rounded-md transition-colors group ${
                              isEnabled
                                ? 'bg-white border-slate-200 hover:border-hr-pink/30 hover:shadow-sm'
                                : 'bg-slate-50 border-slate-100 opacity-60'
                            }`}
                          >
                            <div className="flex items-center gap-2 overflow-hidden">
                              <input
                                type="checkbox"
                                aria-label={`Enable ${ben.type}`}
                                checked={isEnabled}
                                onChange={(e) =>
                                  updateBenefit(catIdx, benIdx, 'isEnabled', e.target.checked)
                                }
                                className="w-4 h-4 rounded border-slate-300 text-hr-pink focus:ring-hr-pink cursor-pointer"
                              />
                              <span
                                className={`font-bold text-xs truncate ${
                                  isEnabled ? 'text-hr-indigo' : 'text-slate-400'
                                }`}
                              >
                                {ben.type}
                              </span>
                            </div>

                            {isEnabled ? (
                              <>
                                <div className="flex gap-2 w-full items-center">
                                  {(ben.type === BenefitType.Death ||
                                    ben.type === BenefitType.PTD) && (
                                    <>
                                      {cat.method === CalculationMethod.Stated ? (
                                        <div className="flex-1 min-w-0 max-w-[200px]">
                                          <div className="flex items-center h-8 border border-slate-300 rounded px-2 bg-slate-50 focus-within:bg-white shadow-sm focus-within:ring-2 focus-within:ring-hr-pink w-full">
                                            <input
                                              className="w-full border-none bg-transparent text-center text-xs font-semibold text-slate-800 focus:ring-0 outline-none h-full"
                                              type="number"
                                              aria-label="Salary multiple"
                                              value={ben.multiple ?? 1}
                                              onChange={(e) =>
                                                updateBenefit(
                                                  catIdx,
                                                  benIdx,
                                                  'multiple',
                                                  parseFloat(e.target.value),
                                                )
                                              }
                                              onFocus={handleFocus}
                                            />
                                            <span className="text-xs text-slate-500 whitespace-nowrap px-2 border-l border-slate-200 h-full flex items-center">
                                              x Salary
                                            </span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex-1 min-w-0 max-w-[200px]">
                                          <MoneyInput
                                            value={ben.sumInsured ?? 0}
                                            onChange={(v) =>
                                              updateBenefit(catIdx, benIdx, 'sumInsured', v)
                                            }
                                            className="h-8 text-xs w-full bg-slate-50"
                                          />
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {ben.type === BenefitType.TTD && (
                                    <>
                                      {cat.method === CalculationMethod.Stated ? (
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center h-8 border border-slate-300 rounded px-2 bg-slate-50 focus-within:bg-white shadow-sm focus-within:ring-2 focus-within:ring-hr-pink w-full">
                                            <input
                                              className="w-full border-none bg-transparent text-center text-xs font-semibold text-slate-800 focus:ring-0 outline-none h-full"
                                              type="number"
                                              aria-label="Salary percent"
                                              value={ben.salaryPct ?? 0}
                                              onChange={(e) =>
                                                updateBenefit(
                                                  catIdx,
                                                  benIdx,
                                                  'salaryPct',
                                                  parseFloat(e.target.value),
                                                )
                                              }
                                              onFocus={handleFocus}
                                            />
                                            <span className="text-xs text-slate-500 whitespace-nowrap px-2 border-l border-slate-200 h-full flex items-center">
                                              %
                                            </span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex-1 min-w-0">
                                          <MoneyInput
                                            value={ben.weeklyBenefit ?? 0}
                                            onChange={(v) =>
                                              updateBenefit(catIdx, benIdx, 'weeklyBenefit', v)
                                            }
                                            className="h-8 w-full text-xs bg-slate-50"
                                          />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <select
                                          aria-label="Benefit period"
                                          className={`${selectBaseClass} w-full`}
                                          value={ben.benefitPeriod ?? 104}
                                          onChange={(e) =>
                                            updateBenefit(
                                              catIdx,
                                              benIdx,
                                              'benefitPeriod',
                                              Number(e.target.value) as 52 | 104 | 156,
                                            )
                                          }
                                        >
                                          <option value={52}>52 Wk</option>
                                          <option value={104}>104 Wk</option>
                                        </select>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <select
                                          aria-label="Excess days"
                                          className={`${selectBaseClass} w-full`}
                                          value={ben.excessDays ?? 7}
                                          onChange={(e) =>
                                            updateBenefit(
                                              catIdx,
                                              benIdx,
                                              'excessDays',
                                              Number(e.target.value) as 7 | 14 | 28 | 60,
                                            )
                                          }
                                        >
                                          <option value={7}>7 Days</option>
                                          <option value={14}>14 Days</option>
                                          <option value={28}>28 Days</option>
                                          <option value={60}>60 Days</option>
                                        </select>
                                      </div>
                                    </>
                                  )}

                                  {ben.type === BenefitType.Medical && (
                                    <>
                                      <div className="flex-[1.5] min-w-0">
                                        <MoneyInput
                                          value={ben.medicalLimit ?? 0}
                                          onChange={(v) =>
                                            updateBenefit(catIdx, benIdx, 'medicalLimit', v)
                                          }
                                          className="h-8 w-full text-xs bg-slate-50"
                                        />
                                      </div>
                                      <div className="flex-1 min-w-[70px]">
                                        <select
                                          aria-label="Medical excess"
                                          className={`${selectBaseClass} w-full`}
                                          value={ben.medicalExcess ?? 500}
                                          onChange={(e) =>
                                            updateBenefit(
                                              catIdx,
                                              benIdx,
                                              'medicalExcess',
                                              Number(e.target.value) as
                                                | 0
                                                | 250
                                                | 500
                                                | 1000
                                                | 2500
                                                | 5000,
                                            )
                                          }
                                        >
                                          <option value={0}>R0</option>
                                          <option value={250}>R250</option>
                                          <option value={500}>R500</option>
                                          <option value={1000}>R1000</option>
                                          <option value={2500}>R2500</option>
                                        </select>
                                      </div>
                                      <div className="w-16 flex-shrink-0">
                                        <select
                                          aria-label="Medical basis"
                                          className="h-8 border-none bg-indigo-50 text-indigo-700 font-bold p-1 rounded focus:ring-0 cursor-pointer hover:bg-indigo-100 transition-colors text-xs w-full text-center"
                                          value={
                                            ben.medicalBasis ??
                                            (cat.method === CalculationMethod.Fixed
                                              ? 'Fixed'
                                              : 'Stated')
                                          }
                                          onChange={(e) =>
                                            updateBenefit(
                                              catIdx,
                                              benIdx,
                                              'medicalBasis',
                                              e.target.value as 'Fixed' | 'Stated',
                                            )
                                          }
                                        >
                                          <option value="Fixed">Fix</option>
                                          <option value="Stated">%</option>
                                        </select>
                                      </div>
                                    </>
                                  )}
                                </div>

                                <div className="h-8 border border-slate-200 bg-slate-50 rounded-md px-1 flex items-center justify-between gap-1 shadow-sm w-full">
                                  <div className="flex items-center flex-1 justify-center bg-white rounded border border-slate-100 h-6 px-1">
                                    <input
                                      type="number"
                                      aria-label="Benefit discretionary adjustment"
                                      className="w-8 text-right bg-transparent text-xs font-bold text-slate-800 focus:outline-none p-0 border-none"
                                      value={ben.discretionaryAdjustment ?? 0}
                                      onChange={(e) =>
                                        updateBenefit(
                                          catIdx,
                                          benIdx,
                                          'discretionaryAdjustment',
                                          parseFloat(e.target.value),
                                        )
                                      }
                                      onFocus={handleFocus}
                                    />
                                    <span className="text-micro text-slate-400 font-bold ml-0.5">
                                      %
                                    </span>
                                  </div>
                                  <div
                                    className="px-1 text-xs font-bold text-slate-500 truncate text-center w-10"
                                    title={`System Net Multiplier: ${netMultiplier.toFixed(4)}`}
                                  >
                                    {discountPct > 0
                                      ? `-${discountPct.toFixed(0)}%`
                                      : discountPct < 0
                                        ? `+${Math.abs(discountPct).toFixed(0)}%`
                                        : 'Std'}
                                  </div>
                                </div>

                                <div className="h-8 border border-slate-100 rounded px-2 bg-slate-50/50 flex items-center justify-end text-xs w-full overflow-hidden text-slate-500 font-medium whitespace-nowrap">
                                  {cat.method === CalculationMethod.Stated ? (
                                    <span className="truncate">
                                      R{' '}
                                      {exposure.toLocaleString(undefined, {
                                        maximumFractionDigits: 0,
                                      })}{' '}
                                      × {effectiveRateDisplay}
                                    </span>
                                  ) : (
                                    <span className="truncate">
                                      {exposure} × {effectiveRateDisplay}
                                    </span>
                                  )}
                                </div>

                                <div className="text-right font-bold text-slate-900 text-xs tracking-tight w-full truncate">
                                  {isCalculating ? (
                                    <span className="animate-pulse text-slate-300">…</span>
                                  ) : (
                                    `${CURRENCY_SYMBOLS[context.policy_parameters.currency]} ${(ben.netPremium || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className="col-span-4 text-xs text-slate-300 italic pl-2">
                                Benefit Excluded from Quote
                              </div>
                            )}
                            <div className="w-7" />
                          </div>
                        );
                      })}
                    </div>

                    {cat.benefits.some((b) => b.isEnabled !== false) && (
                      <div className="mt-6 bg-slate-100 rounded-lg px-6 py-4 flex justify-between items-center border border-slate-200 shadow-inner">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                          Total Category Premium
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="hidden md:flex items-center gap-1.5 text-xs font-medium text-slate-500">
                            <span className="text-slate-700 font-bold bg-white px-2 py-0.5 rounded border border-slate-200 shadow-sm">
                              {footerExposureDisplay}
                            </span>
                            <span className="text-slate-400">×</span>
                            <span className="text-slate-700 font-bold bg-white px-2 py-0.5 rounded border border-slate-200 shadow-sm">
                              {footerRateDisplay}
                            </span>
                            <span className="text-xs uppercase text-slate-400 ml-1">
                              (Total Rate)
                            </span>
                            <span className="text-slate-400 ml-1">=</span>
                          </div>
                          <div className="text-lg font-black tracking-tight flex items-baseline gap-1 text-slate-800">
                            <span className="text-xs font-bold text-slate-500">
                              {CURRENCY_SYMBOLS[context.policy_parameters.currency]}
                            </span>
                            {isCalculating
                              ? '…'
                              : categoryTotal.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={addCategory}
                className="w-full max-w-sm bg-slate-50 border-2 border-dashed border-hr-pink/30 text-hr-pink/80 px-6 py-4 rounded-xl shadow hover:bg-hr-pink hover:text-white hover:border-transparent transition-colors text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2"
              >
                + Create New Category
              </button>
            </div>

            {context.categories.length === 0 && (
              <div className="text-center py-16 border-2 border-dashed border-slate-300 rounded-xl text-slate-400 bg-slate-50">
                <p className="font-medium">No Rating Categories Defined</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 5: UNDERWRITING NOTES */}
      <section id="underwriting-notes" className="scroll-mt-36">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-soft space-y-6">
          <SectionHeader number="5" title="Underwriting Notes" />

          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <label
              htmlFor="internal-notes"
              className="block text-micro font-bold text-amber-800 uppercase tracking-wide mb-2 flex justify-between"
            >
              <span>Internal Notes &amp; Justification</span>
              <span className="bg-amber-200 text-amber-900 px-2 py-0.5 rounded text-micro">
                NOT ON PDF
              </span>
            </label>
            <textarea
              id="internal-notes"
              className="w-full h-32 py-3 px-4 bg-white border border-amber-200 rounded-lg text-slate-800 text-xs font-medium focus:ring-2 focus:ring-amber-400 outline-none resize-none shadow-inner"
              placeholder="Auto-populated with discount logic. Add further internal justification here."
              value={context.client_profile.underwritingNotes ?? ''}
              onChange={(e) => updateClient('underwritingNotes', e.target.value)}
              onFocus={handleFocus}
            />
            <p className="text-micro text-amber-700 mt-2 font-medium">
              Records specific rating factors, applied discounts, and internal rationale for audit
              purposes.
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <label
              htmlFor="quote-notes"
              className="block text-micro font-bold text-hr-indigo uppercase tracking-wide mb-2 flex justify-between"
            >
              <span>External Quote Conditions</span>
              <span className="bg-hr-indigo text-white px-2 py-0.5 rounded text-micro">
                VISIBLE ON PDF
              </span>
            </label>
            <textarea
              id="quote-notes"
              className="w-full h-32 py-3 px-4 bg-white border border-slate-300 rounded-lg text-slate-800 text-xs font-medium focus:ring-2 focus:ring-hr-pink outline-none resize-none shadow-sm transition-colors"
              placeholder="e.g. Subject to Sanctions check. Quotation valid for 30 days. Specific exclusions apply."
              value={context.client_profile.quoteNotes ?? ''}
              onChange={(e) => updateClient('quoteNotes', e.target.value)}
              onFocus={handleFocus}
            />
            <p className="text-micro text-slate-400 mt-2 text-right">
              These notes will be printed on the formal Quote Proposal PDF.
            </p>
          </div>
        </div>
      </section>

      {context.pricing_summary.warnings && context.pricing_summary.warnings.length > 0 && (
        <div className="mt-8 bg-white border-l-4 border-hr-pink p-6 rounded-xl shadow-soft border border-slate-200">
          <h3 className="text-sm font-black text-hr-indigo tracking-tight">Governance Warnings</h3>
          <ul className="mt-2 list-disc pl-5 space-y-2 text-xs text-slate-600 font-medium">
            {context.pricing_summary.warnings.map((w, i) => (
              <li key={`${i}-${w.slice(0, 10)}`}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
